/**
 * Monitor rules for the "Major problems" and "Major changes" tiles
 * (quick-261104-ia3, John 2026-11-04). Pure. Each item names the mechanism, not
 * a person or team (standing rule: no blame framing).
 */
import { change, dayLabel, money, monthLabel, num, pct, rate } from './fmt.js';
import { FY2027_PIPELINE_TARGET } from './links.js';
import { bucketSeries } from './periods.js';
import { rilletFrom } from './rillet-source.js';

import type { ExecResponse, MainScorecardResponse } from '@saledash/shared';

export type Severity = 'critical' | 'serious' | 'warning';

export interface ProblemItem {
  severity: Severity;
  title: string;
  detail: string;
  /** Tab to jump to for the detail. */
  tab?: 'sales' | 'marketing' | 'cs' | 'services' | 'finance';
  /** Opens the potential-opps audit list instead of a tab. */
  opensPotential?: boolean;
}

export interface ChangeItem {
  kind: 'won' | 'lost' | 'created' | 'advanced' | 'slipped' | 'arr' | 'pipeline';
  title: string;
  detail: string;
  date: string;
  amount: number;
  good: boolean | null;
  href?: string;
}

const SEV_RANK: Record<Severity, number> = { critical: 0, serious: 1, warning: 2 };

export function computeProblems(
  exec: ExecResponse | undefined,
  scorecard: MainScorecardResponse | undefined,
  today: string,
): ProblemItem[] {
  const out: ProblemItem[] = [];
  const panels = exec?.panels;
  const R = rilletFrom(exec);

  // 1. Current-quarter coverage (MAIN-13 value vs its 3× net-of-won target).
  const cq = scorecard?.metrics['MAIN-13'];
  if (cq?.value != null && cq.target_value != null && cq.target_value > 0) {
    const r = cq.value / cq.target_value;
    if (r < 1) {
      out.push({
        severity: r < 0.6 ? 'critical' : 'serious',
        title: `This quarter's pipeline covers ${pct(r)} of what we need`,
        detail: `${money(cq.value)} open against a ${money(cq.target_value)} coverage target (3× the quota still to close).`,
        tab: 'sales',
      });
    }
  }

  // 2. Next-year pipeline vs the temporary $3.5M target.
  const cov = panels?.coverage?.payload;
  if (cov) {
    const nextYear = Number(today.slice(0, 4)) + 1;
    const fy = cov.cells.filter((c) => c.year === nextYear).reduce((s, c) => s + c.open, 0);
    const r = fy / FY2027_PIPELINE_TARGET;
    if (r < 1) {
      out.push({
        severity: r < 0.5 ? 'serious' : 'warning',
        title: `FY${String(nextYear % 100)} pipeline is ${pct(r)} of the ${money(FY2027_PIPELINE_TARGET)} target`,
        detail: `${money(fy)} open with ${String(nextYear)} close dates. Target is temporary until the plan is final.`,
        tab: 'sales',
      });
    }
  }

  // 3. Churn in the latest month.
  const lastM = R.months[R.months.length - 1];
  if (lastM && lastM.churnArr >= 100_000) {
    const names = lastM.churned.slice(0, 3).map((c) => c.name).join(', ');
    out.push({
      severity: 'critical',
      title: `${money(lastM.churnArr)} of ARR dropped off in ${monthLabel(lastM.month)}`,
      detail: `${names}${lastM.month === R.provisionalMonth ? '. Provisional: confirm whether these are losses or renewals not yet booked in Rillet.' : '.'}`,
      tab: 'cs',
    });
  }

  // 4. Renewals due in 90 days with no successor contract yet.
  const in90 = R.renewals.filter((r) => !r.renewed && r.endDate >= today && r.endDate <= addDaysIso(today, 90));
  if (in90.length > 0) {
    const v = in90.reduce((s, r) => s + r.value, 0);
    out.push({
      severity: 'serious',
      title: `${num(in90.length)} contracts end in the next 90 days with no renewal booked`,
      detail: `${money(v)} of contract value. Earliest: ${in90[0]?.customer ?? ''} on ${dayLabel(in90[0]?.endDate ?? today)}.`,
      tab: 'cs',
    });
  }

  // 5. Stalled deals (MAIN-21).
  const stalled = scorecard?.metrics['MAIN-21'];
  if (stalled?.value != null && stalled.value >= 5) {
    out.push({
      severity: stalled.value >= 10 ? 'serious' : 'warning',
      title: `${num(stalled.value)} open deals have stalled`,
      detail: 'No stage change and no next-step update in 21+ days.',
      tab: 'sales',
    });
  }

  // 6. Potential opps waiting.
  const funnel = panels?.funnel?.payload;
  if (funnel && funnel.potential.length >= 5) {
    out.push({
      severity: 'warning',
      title: `${num(funnel.potential.length)} engaged companies have no opportunity yet`,
      detail: `Worth about ${money(exec?.avgDealSize != null ? funnel.potential.length * exec.avgDealSize : null)} at our average deal size. Click to audit the list.`,
      opensPotential: true,
    });
  }

  // 7. Meetings booked: last 4 complete weeks vs the 4 before.
  const act = panels?.activity?.payload;
  if (act) {
    const w = bucketSeries(act.meetingsBooked, 'week', today, undefined, 9).filter((b) => !b.partial);
    const recent = w.slice(-4).reduce((s, b) => s + b.c, 0);
    const prior = w.slice(-8, -4).reduce((s, b) => s + b.c, 0);
    const c = change(recent, prior);
    if (c?.dir === 'down' && prior > 0 && recent / prior < 0.8) {
      out.push({
        severity: 'serious',
        title: `New meetings booked are down ${c.text.replace('−', '')} over the last 4 weeks`,
        detail: `${num(recent)} booked vs ${num(prior)} in the 4 weeks before.`,
        tab: 'sales',
      });
    }
  }

  // 8. New-logo win rate: latest complete quarter below the prior four.
  const wr = panels?.winrate?.payload;
  if (wr && wr.quarters.length >= 3) {
    const done = wr.quarters.slice(0, -1);
    const lastQ = done[done.length - 1];
    const prior = done.slice(-5, -1);
    const pw = prior.reduce((s, q) => s + q.newWon, 0);
    const pl = prior.reduce((s, q) => s + q.newLost, 0);
    const lr = lastQ ? rate(lastQ.newWon, lastQ.newLost) : null;
    const pr = rate(pw, pl);
    if (lr != null && pr != null && lastQ && lr < pr * 0.8) {
      out.push({
        severity: 'warning',
        title: `New-logo win rate fell to ${pct(lr)} in ${lastQ.label}`,
        detail: `Down from ${pct(pr)} over the four quarters before.`,
        tab: 'sales',
      });
    }
  }

  // (Rule 9 "renewal records past their close date" removed 2026-11-04 — John:
  // a data-hygiene item, not a major business problem.)

  return out.sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity]);
}

export function computeChanges(exec: ExecResponse | undefined): ChangeItem[] {
  const out: ChangeItem[] = [];
  const R = rilletFrom(exec);
  const ev = exec?.panels.changes?.payload.events ?? [];
  for (const e of ev) {
    const who = e.account ?? e.name;
    if (e.kind === 'won') {
      out.push({ kind: 'won', title: `Won ${who}`, detail: `${money(e.amount)} · ${e.type ?? ''}`, date: e.date, amount: e.amount, good: true, href: e.id });
    } else if (e.kind === 'lost') {
      out.push({ kind: 'lost', title: `Lost ${who}`, detail: `${money(e.amount)} · ${e.type ?? ''}`, date: e.date, amount: e.amount, good: false, href: e.id });
    } else if (e.kind === 'created' && e.amount >= 25_000) {
      out.push({ kind: 'created', title: `New opportunity: ${who}`, detail: `${money(e.amount)} · ${e.owner ?? ''}`, date: e.date, amount: e.amount, good: true, href: e.id });
    } else if (e.kind === 'advanced' && e.amount >= 50_000) {
      out.push({ kind: 'advanced', title: `${who} moved forward`, detail: `${e.detail ?? ''} · ${money(e.amount)}`, date: e.date, amount: e.amount, good: true, href: e.id });
    } else if (e.kind === 'slipped' && e.amount >= 50_000) {
      out.push({ kind: 'slipped', title: `${who} pushed its close date`, detail: `${e.detail ?? ''} · ${money(e.amount)}`, date: e.date, amount: e.amount, good: false, href: e.id });
    }
  }

  // Pipeline vs last month-end.
  const f = exec?.panels.funnel?.payload;
  const prior = f?.history[f.history.length - 1];
  if (f && prior) {
    const now = Object.values(f.current.byStage).reduce((s, v) => s + v, 0);
    const then = Object.values(prior.byStage).reduce((s, v) => s + v, 0);
    const d = now - then;
    if (Math.abs(d) >= 100_000) {
      out.push({
        kind: 'pipeline',
        title: `Open pipeline ${d > 0 ? 'up' : 'down'} ${money(Math.abs(d))} since ${monthLabel(prior.asOf.slice(0, 7))} close`,
        detail: `${money(then)} → ${money(now)}`,
        date: f.current.asOf,
        amount: Math.abs(d),
        good: d > 0,
      });
    }
  }

  // ARR last month.
  const last = R.months[R.months.length - 1];
  const prev = R.months[R.months.length - 2];
  if (last && prev) {
    const d = last.arr - prev.arr;
    out.push({
      kind: 'arr',
      title: `ARR ${d >= 0 ? 'up' : 'down'} ${money(Math.abs(d))} in ${monthLabel(last.month)}`,
      detail: `${money(prev.arr)} → ${money(last.arr)}${last.month === R.provisionalMonth ? ' (provisional)' : ''}`,
      date: `${last.month}-28`,
      amount: Math.abs(d),
      good: d >= 0,
    });
  }

  return out.sort((a, b) => b.date.localeCompare(a.date) || b.amount - a.amount);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
